import type { PlanningStage } from '@/lib/planning-types'
import { cn } from '@/lib/utils'
import { BAND_FILL, BAND_LABEL } from './CapacityBar'
import { STAGE_FILTERS, toggleStage } from './stages'

/**
 * Welke zekerheid je meetelt in de maanden eronder. Een meervoudige keuze, dus
 * geen segmented control: die staat voor een keuze waarbij het een het ander
 * uitsluit, en hier tellen trappen juist samen op.
 *
 * De vorm is de tweede laag uit "filter in twee lagen": de horizonkeuze in de
 * kaartkop voert de omlijnde vorm, dus deze rij is vlak. Elke knop draagt het
 * blokje van zijn eigen band uit de dichtheidsladder, zodat de knop en de band in
 * de balk herkenbaar hetzelfde ding zijn; uit is datzelfde blokje verbleekt, want
 * de trap verdwijnt niet, hij telt alleen even niet mee.
 */
export function StageFilter({
  value,
  onChange,
}: {
  value: PlanningStage[]
  onChange: (stages: PlanningStage[]) => void
}) {
  return (
    <div
      role="group"
      aria-label="Wat telt mee in de maanden"
      className="flex flex-wrap items-center gap-x-1 gap-y-1.5 border-b border-hairline px-5 py-2.5"
    >
      <span className="label-caps mr-1.5">Telt mee</span>
      {STAGE_FILTERS.map(({ value: stage, band }) => {
        const active = value.includes(stage)
        // De laatste die aanstaat ligt vast: zonder trap is er geen vooruitblik,
        // alleen een scherm vol nullen. Met `aria-disabled` en niet `disabled`,
        // anders is de uitleg waarom hij vastligt onbereikbaar.
        const locked = active && value.length === 1

        return (
          <button
            key={stage}
            type="button"
            aria-pressed={active}
            aria-disabled={locked || undefined}
            title={locked ? 'Er moet minstens een trap meetellen' : undefined}
            onClick={() => onChange(toggleStage(value, stage))}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40',
              active ? 'bg-fill-2 font-medium text-ink' : 'text-ink-faint hover:bg-fill-1',
              locked && 'cursor-default',
            )}
          >
            <span aria-hidden className={cn('h-2 w-2 shrink-0 rounded-xs', BAND_FILL[band], !active && 'opacity-25')} />
            {BAND_LABEL[band]}
          </button>
        )
      })}
    </div>
  )
}
